/**
 * @file        src/api/flights.js
 * @description Flight API client + response adapters. Wraps the backend's
 *              TripJack v1 air flow (mounted under /api/v1):
 *                air-search → (review + book happen server-side in the Easebuzz
 *                callback) → flight-payment/initiate.
 *              Adapters reshape the v1 air-search payload
 *              (searchResult.tripInfos.ONWARD[].{ sI, totalPriceList }) into the
 *              plain flight-card objects the existing static FlightResults /
 *              FlightCard views already consume (f.airline, f.dep, f.price …) so
 *              the UI markup stays untouched, while carrying the live booking
 *              keys (priceId, supplierPrice) the checkout needs on `_raw`/extra
 *              fields.
 *
 *              Price model (see marginRuleService): TripJack returns the net fare
 *              in fC.TF; the backend writes the marked-up customer price into
 *              fC.FP. The customer ALWAYS sees FP; TripJack is paid TF.
 * @author      Kinetika Labs
 * @created     2026-06-24
 * @changelog
 *   - 2026-10-08 Claude — legGapIssue() / legGapMessage(): TripJack will not sell
 *                          two consecutive flights of one trip when the second
 *                          leaves less than 2 hours after the first lands. Its
 *                          review refuses the pair ("Minimum time between two
 *                          consecutive trips does not satisfy the criteria"), and
 *                          the booking page reported that as "Flight no longer
 *                          available". FlightResults now asks this before VIEW
 *                          PRICES, the way TripJack's own results page does.
 *   - 2026-10-06 Claude — checkFlightCoupon refuses a code whose Domestic /
 *                          International scope (admin migration 076) misses the
 *                          trip with reason 'domestic_only' / 'international_only',
 *                          so the customer is told which trips it is for.
 *   - 2026-10-05 Claude — fetchFlightOffers carries `convenienceFees` (every
 *                          candidate fee rule, each with its Airline / Fare Type
 *                          scope), and checkFlightCoupon(code, legs) answers the
 *                          new reason 'fare' for a code scoped to an airline or
 *                          fare family the chosen trip is not on.
 *   - 2026-09-25 Claude — Every fare and card carries `expiresAt`: when the search
 *                          that minted its priceId stops being bookable. Recorded per
 *                          search TAG by searchTagOf() off the backend's new
 *                          `fareValidity` stamp, rather than threaded as one more
 *                          parameter — "which search minted this" is a question
 *                          searchTag already answers, and a deadline belongs to a
 *                          search. null where a backend sent no stamp; callers must
 *                          read that as "unknown", never as "expired".
 *   - 2026-09-25 Claude — Fare type is MULTI-SELECT (Regular / Student / Senior
 *                          Citizen). TripJack takes ONE `pft` per search, so
 *                          searchFlightsByFareTypes() runs one /air-search per ticked
 *                          type in parallel and concatenates the trips into one
 *                          response. Every price option is stamped with the search
 *                          that minted it (`_searchTag`) and the type it was searched
 *                          under (`_pft`), and faresOf / toFlightCard read those
 *                          stamps in preference to the response-level tag — a priceId
 *                          is still only bookable beside fares of its own search.
 *                          groupFlightCards collapses a fare that two searches both
 *                          returned identically into ONE row (the Regular copy wins,
 *                          since it is not discounted) and keeps the others on
 *                          `shadowFares`, so the results page can still move a leg
 *                          onto another search's copy of the same fare. A single
 *                          ticked type is one search, exactly as before.
 *   - 2026-09-22 Claude — An OFFER fare is NAMED "Neon Exclusive Fare" — the fare
 *                          TYPE itself, not a badge beside it. fareTypeLabel() is that
 *                          one mapping and fareName() ends in it, so the storefront
 *                          never prints the supplier's own "Offer fare with pnr" —
 *                          which is a TripJack booking mechanic, not a product name a
 *                          customer can be asked to choose between.
 *   - 2026-09-22 Claude — isGstMandatoryFare() / gstMandateFor(): a GSTIN is
 *                          COMPULSORY only on an IndiGo (6E) CORPORATE or SME fare
 *                          (client decision). Replaces isCorporateFare() as the GST
 *                          gate on FlightBook + FlightReview, which demanded one on
 *                          EVERY airline's corporate fare and on no SME fare at all.
 *                          Prefers the server's own answer (gstMandatory, off the
 *                          review) — this is the fallback, and initiate's 409
 *                          GST_REQUIRED is the boundary behind both.
 *   - 2026-09-22 Claude — isExclusiveFare(): TripJack's OFFER / OFFER_FARE_WITH_PNR,
 *                          matched on the prefix like isCorporateFare normalises
 *                          spellings. The fare popup badges these as the storefront's
 *                          own exclusive fare and prints their booking conditions.
 *   - 2026-09-20 Claude — isCorporateFare(): ONE test for "is this a corporate
 *                          fare?", replacing the `id === 'CORPORATE'` copies in
 *                          FlightBook and FlightReview. Those matched neither
 *                          CORPORATE_FARE (TripJack returns both spellings — see
 *                          FARE_LABEL, which has mapped both since 2026-09-19) nor
 *                          the misspelled `fareIdentifer` key, so a corporate fare
 *                          read as ordinary and its MANDATORY GST gate never armed:
 *                          Continue went straight to review with no GSTIN.
 *   - 2026-09-19 Claude — fareName() reads TripJack's misspelled `fareIdentifer` as well
 *                          as `fareIdentifier` (the backend always has), and its
 *                          unmapped fallback no longer prints the raw supplier enum —
 *                          a live e-ticket read "Economy · Corporate_fare". CORPORATE_FARE
 *                          is mapped to "Corporate" alongside CORPORATE.
 *   - 2026-09-19 Claude — pairableFares(): which of a leg's fares can still be sold
 *                          next to the fare already chosen on an earlier leg. The
 *                          Special Return rules were only ever applied AFTER the fact
 *                          (specialReturnIssue → popup); this is the same rules turned
 *                          round so the results list and the fare modal never OFFER
 *                          the unbookable half. The popup stays as the backstop.
 *   - 2026-09-15 Claude — paxConditions now documents `idAllowed`/`idRequired`, the
 *                          CONCESSION ID CARD flags (review conditions.dc), kept
 *                          separate from the passport ones — conflating the two made
 *                          a passport compulsory on Student / Senior Citizen fares.
 *   - 2026-09-14 Claude — Special Return pairing for ROUND TRIP. faresOf() now carries
 *                          the supplier's `sri` / `msri` pairing codes and the fare's
 *                          `airlineCode`, and specialReturnIssue() says why two fares
 *                          can't be sold together (BOTH_SPECIAL / SAME_AIRLINE /
 *                          NOT_PAIRED) — rules verified live on TripJack UAT, see the
 *                          note above the function. FARE_LABEL gains SPECIAL_RETURN,
 *                          which used to print as "Special_return" on the fare pill.
 *   - 2026-09-11 Claude — Every fare now carries `searchTag` (new searchTagOf(): which
 *                          /air-search RESPONSE minted it), stamped by faresOf() /
 *                          toFlightCard() / fetchOtherCabinFares(). TripJack reviews a
 *                          trip's priceIds only when they all come from ONE search, and
 *                          the fare modal's per-cabin re-search let a round trip take
 *                          its DEPART fare from the Business search and its RETURN fare
 *                          from the Economy one — refused at the seat map with "Number
 *                          of PriceId passed in the review request doesn't match with
 *                          the requested trips". The stamp is what lets the modal and
 *                          the results page keep a trip on one search.
 *   - 2026-09-11 Claude — ONE CARD PER FLIGHT. adaptSearchResults() now merges every
 *                          entry of the same physical flight (same tripSignature:
 *                          airline + flight number + departure date-and-time, per
 *                          segment) into one card via groupFlightCards(), its
 *                          `fares` the union of all their fare families. TripJack
 *                          returns one entry per content source, so AI2430 10:00
 *                          was showing as several cards. Fares from a non-headline
 *                          entry carry that entry's trip on `src`, and the new
 *                          cardForFare() books them on it — entries of one flight
 *                          can disagree on arrival time, and booking persists the
 *                          card's `_raw.sI` into the voucher. COMBO searches are
 *                          untouched (comboSearchInfo, already one card per sig).
 *   - 2026-09-08 Claude — fetchBaggageOptions passes through `freeBaggage`, the
 *                          INCLUDED allowance the reviewed fare carries. The
 *                          airline can change it between search and review and we
 *                          showed nothing, so the customer booked a fare whose
 *                          advertised 30 Kg had already become 20 Kg.
 *   - 2026-09-08 Claude — Added FARE_COMBINATION_INVALID / FARE_SELECTION_INCOMPLETE
 *                          (+ predicates). A round trip can fail on the fare
 *                          COMBINATION rather than on any one flight — a Special
 *                          Return fare covers both legs, so it cannot be paired
 *                          with an ordinary one — and the backend now says so
 *                          instead of reporting it as a sold-out flight.
 *   - 2026-09-02 Claude — toFlightCard()'s summary duration (`dur`/`durMin`) no
 *                          longer diffs the trip's raw dep/arr timestamps
 *                          end-to-end. TripJack's dt/at are zone-less local
 *                          strings, so a naive Date subtraction silently
 *                          drops the origin/destination UTC-offset gap
 *                          whenever they differ (e.g. BOM→DXB showed "1h 55m"
 *                          instead of the real 3h 25m — IST is 1.5h ahead of
 *                          GST). New tripDurationMinutes() sums each
 *                          segment's own supplier-reported `duration`
 *                          (already timezone-correct per leg, same field the
 *                          expanded Flight Information panel trusts) plus the
 *                          ground time between segments, which IS safe to
 *                          diff naively since a layover's two timestamps share
 *                          one airport/zone. Only the whole-trip endpoint-to-
 *                          endpoint diff was wrong; per-segment/layover diffs
 *                          are untouched.
 *   - 2026-08-21 Claude — fetchBaggageOptions() no longer swallows an unclassified
 *                          review failure into a fallback with no `mandatory` key.
 *                          It re-throws on ANY error now (not only FARE_EXPIRED /
 *                          UPFRONT_SEAT_UNAVAILABLE), because `mandatory` decides
 *                          whether the booking form demands a seat — silently
 *                          answering "not mandatory" for an unrelated failure let a
 *                          seat-mandatory fare through the whole form unseated.
 *                          FlightBook now blocks with FareExpiredModal on any
 *                          failure here instead of guessing.
 *   - 2026-08-18 Claude — Retired carriers (Vistara UK, Go First G8) are dropped
 *                          from every search response — one filter at the trip ->
 *                          card boundary, covering per-leg and COMBO results, so
 *                          the list, the airline facet and the booking flow can
 *                          never surface an airline we no longer sell. Historical
 *                          bookings keep their brand colour and logo.
 *   - 2026-08-14 Claude — fetchBaggageOptions() carries the backend's new
 *                          `seatMandatorySegmentIds` through to the booking form,
 *                          so a mandatory seat (IndiGo Upfront `ism`) can be
 *                          demanded on the sectors that require one rather than on
 *                          every sector. Absent on an older backend → empty array,
 *                          and the form falls back to its previous behaviour.
 *   - 2026-08-04 Claude — CR B1: checkFlightCoupon() now returns { row, reason }
 *                          instead of a bare row-or-null, forwarding the backend's
 *                          distinguishing reason ('invalid' | 'expired' |
 *                          'exhausted' | 'scope') so FlightResults (new step-1
 *                          promo box) and FlightPayment (re-validation before
 *                          charge) can show a SPECIFIC message instead of one
 *                          generic "invalid" line. Same endpoint, same validation
 *                          — only the response is now more informative.
 *   - 2026-08-03 Claude — Map fd.ADULT.cB as bookingClass (the RBD). Distinct from
 *                          fd.ADULT.bI.cB one level down, which is the cabin-baggage
 *                          allowance — same key, unrelated meaning.
 *   - 2026-07-31 Claude — comboSearchInfo(): international round trip / multi-city.
 *                          TripJack returns those under ONE `COMBO` key where each
 *                          entry is a COMPLETE journey covering every requested leg —
 *                          one fare, one priceId, sold as a unit — not per-leg lists.
 *                          The response is now modelled as PAIRS: one card per leg per
 *                          combo (all sharing that combo's priceId) plus the signatures
 *                          the results page pair-locks the columns with. Splitting is by
 *                          searched route + date, with `isRs` only as a fallback (it is
 *                          absent on 367 of 459 live multi-city entries). An earlier
 *                          same-day attempt to split by `isRs` alone let two DIFFERENT
 *                          complete journeys be selected as if they were two legs and
 *                          their whole-trip fares added: quoted ₹47,029.28, supplier
 *                          priced ₹17,318.28 (CART_CHANGED stopped the charge).
 *   - 2026-09-15 Claude — cartChangeInfo() carries the server's `breakdown`,
 *                          `newSubtotal` and `couponApplied`. CART_CHANGED's
 *                          `newTotal` is now a GRAND total (minus insurance, which
 *                          the server will not price on a stopping path), so the
 *                          modal stops labelling a subtotal "New total to pay";
 *                          `newSubtotal` is what the retry must send as `amount`.
 *                          Falls back to the old single-figure shape.
 *   - 2026-07-28 Claude — CART_CHANGED + cartChangeInfo(): the server now refuses to
 *                          charge a cart it has rewritten (add-on the fare cannot
 *                          carry, or a total that drifted from the quote) and returns
 *                          what changed for the customer to confirm.
 *   - 2026-07-27 Claude — PAX_MISMATCH + isPaxMismatch(): the backend now refuses an
 *                          initiate whose fare was priced for a different passenger
 *                          mix than the travellers (a stale selection kept after the
 *                          pax count changed). TripJack used to surface this only as
 *                          errCode 1000 "Requested flight is no longer available".
 *   - 2026-07-20 Claude — Added friendlyFlightError(): rewrites TripJack validation
 *                          messages for a traveller. They arrive as
 *                          "<field path> : <message>", so customers were shown raw
 *                          text like "searchQuery.paxInfo : Infant can't be greater
 *                          than no of adults." Known cases get plain copy that says
 *                          what to do; the rest just lose the field prefix.
 *   - 2026-07-20 Claude — fetchBaggageOptions passes the new `meals` array through
 *                          (absent on older backends -> no meal panel, baggage
 *                          unaffected).
 *   - 2026-06-24 Claude — Initial. Connects the static flight pages to the live
 *                          air-search backend + Easebuzz checkout (one-way).
 *   - 2026-07-12 Claude — isFareExpired(): the backend now tags a review that
 *                          failed on an expired supplier priceId with
 *                          code 'FARE_EXPIRED'. fetchBaggageOptions no longer
 *                          swallows THAT error (it still swallows every other
 *                          failure) so the book page can prompt "search again"
 *                          instead of silently rendering an empty panel.
 *   - 2026-07-12 Claude — fetchOtherCabinFares() is direction-aware. It hardcoded
 *                          tripInfos.ONWARD, so a round trip's RETURN leg (and any
 *                          multi-city leg past the first) found nothing and showed
 *                          no cabin tabs. It now resolves the leg key the same way
 *                          adaptSearchResults does.
 */
import { post, get } from './client.js';
import { ruleFitsTrip, domIntMiss } from '../lib/fareBreakdown.js';

// ---- fare expiry ------------------------------------------------------------

/**
 * A TripJack `priceId` is only valid for a few minutes. Every step after the
 * search re-sends it (baggage options, seat map, payment initiate), so a
 * customer who lingers on the passenger form gets a 409 back. The backend tags
 * exactly that case with `code: 'FARE_EXPIRED'` (utils/fareExpiry) — this is the
 * one signal the SPA needs to stop and offer a fresh search.
 */
export const FARE_EXPIRED = 'FARE_EXPIRED';
// The fare still EXISTS but the airline repriced it UP since the browser quoted it.
// The server refuses to charge the old price and returns the new one so the SPA can
// show it and let the customer re-confirm (booking_backend easebuzzFlightController).
export const PRICE_CHANGED = 'PRICE_CHANGED';
// The fare is GONE — sold out or pulled by the airline (TripJack's "no longer
// available"). Unlike an expired key, re-quoting the same flight won't help; the
// customer has to pick a DIFFERENT flight. Tagged `FARE_UNAVAILABLE` server-side
// (utils/fareExpiry.classifyReviewError).
export const FARE_UNAVAILABLE = 'FARE_UNAVAILABLE';
// An IndiGo Upfront fare whose seat pool cannot cover the party. The fare's price
// INCLUDES a reserved front-of-cabin seat for every adult/child, so the airline
// refuses the whole booking when there aren't enough free — TripJack rejects the
// review itself with errCode 12034. Re-quoting won't help and neither will picking
// a seat: the customer needs a DIFFERENT fare (booking_backend utils/fareExpiry).
export const UPFRONT_SEAT_UNAVAILABLE = 'UPFRONT_SEAT_UNAVAILABLE';
// The fare was priced for a DIFFERENT set of passengers than the travellers being
// booked — the customer changed the pax count after picking a flight and the old
// search's priceIds were still in play. The server compares the review's own
// searchQuery.paxInfo with the travellers and refuses BEFORE charging
// (easebuzzFlightController). Recovery is the same as FARE_UNAVAILABLE: search
// again and re-pick the flight.
export const PAX_MISMATCH = 'PAX_MISMATCH';
// The server could not book the cart as quoted — an add-on the airline will not
// carry on this fare, and/or a total that no longer matches what was on screen.
// It stops BEFORE the gateway and returns what changed; the customer confirms and
// the same request is re-posted with `acceptChanges: true`.
export const CART_CHANGED = 'CART_CHANGED';
// The server's own grand total (subtotal + convenience fee - discounts +
// insurance premium) differs from the one this page displayed. Raised AFTER
// the fee/discount/coupon block, which CART_CHANGED runs before — see
// totalChangeInfo() below.
export const TOTAL_CHANGED = 'TOTAL_CHANGED';
// The legs of this trip carry fares that cannot be sold TOGETHER. TripJack's
// SPECIAL_RETURN is half of one round-trip fare, so it has to be on every leg or
// on none — paired with an ordinary fare on the other leg the review is refused.
// Nothing is wrong with either flight, so "sold out, search again" was the wrong
// story: the fix is a different FARE, on the results page.
export const FARE_COMBINATION_INVALID = 'FARE_COMBINATION_INVALID';
// The review's priceIds don't add up to ONE search's legs: too few of them, or —
// the case customers actually hit — fares from two DIFFERENT searches (a Business
// fare from the fare modal's cabin re-search on one leg, the results page's Economy
// fare on the other). Every fare now carries its search (searchTagOf) and a trip is
// kept on one, so this is the backstop; the way out is to pick the flights again.
export const FARE_SELECTION_INCOMPLETE = 'FARE_SELECTION_INCOMPLETE';

/** True when an axios error (or a plain body) is the expired-fare 409. */
export function isFareExpired(e) {
  const body = e?.response?.data || e?.body || e;
  return body?.code === FARE_EXPIRED;
}

/**
 * Did the supplier refuse this fare because its INCLUDED seats have run out?
 * Like an expired fare this is a dead end, not a missing panel — but the way out
 * is a different fare, not a fresh search.
 */
export function isUpfrontSeatUnavailable(e) {
  const body = e?.response?.data || e?.body || e;
  return body?.code === UPFRONT_SEAT_UNAVAILABLE;
}

/** True when the 409 says the flight itself is no longer available (sold out / pulled). */
export function isFareUnavailable(e) {
  const body = e?.response?.data || e?.body || e;
  return body?.code === FARE_UNAVAILABLE;
}

/**
 * Did the supplier refuse the COMBINATION of fares rather than any one flight?
 * A dead end for this page, like an expired fare — but the way out is the results
 * page (pick fares that can be sold together), not a fresh search.
 */
export function isFareCombinationInvalid(e) {
  const body = e?.response?.data || e?.body || e;
  return body?.code === FARE_COMBINATION_INVALID;
}

/** True when the 409 says a leg of the trip has no flight picked. */
export function isFareSelectionIncomplete(e) {
  const body = e?.response?.data || e?.body || e;
  return body?.code === FARE_SELECTION_INCOMPLETE;
}

/** True when the 409 says the fare was priced for other passengers (PAX_MISMATCH). */
export function isPaxMismatch(e) {
  const body = e?.response?.data || e?.body || e;
  return body?.code === PAX_MISMATCH;
}

/**
 * The cart-changed 409, or null. The server refuses to charge a total the
 * customer has not seen: it returns the add-ons the airline will not carry on
 * this fare and the corrected figures, so the SPA can show both and let the
 * customer decide. Continuing re-posts the SAME cart with `acceptChanges`, which
 * waives only the stop — every price is still the server's.
 *   { dropped: [{ type, label, amount }], newTicketAmount, newTotal, previousTotal, message }
 */
export function cartChangeInfo(e) {
  const body = e?.response?.data || e?.body || e;
  if (body?.code !== CART_CHANGED) return null;
  const bd = body.breakdown || null;
  return {
    dropped: Array.isArray(body.dropped) ? body.dropped : [],
    newTicketAmount: Number(body.newTicketAmount) || 0,
    // Ticket + ancillary SUBTOTAL — what the retry sends back as `amount`. Older
    // servers answered with the subtotal in `newTotal` and no `newSubtotal`; fall
    // back to it so this page keeps working against one.
    newSubtotal: Number(body.newSubtotal ?? body.newTotal) || 0,
    // Grand totals, EXCLUDING the insurance premium (the server will not price a
    // policy on a path that is about to stop). The page adds the premium it is
    // already displaying — see cartChangedView().
    newTotal: Number(body.newTotal) || 0,
    previousTotal: Number(body.previousTotal) || 0,
    breakdown: bd
      ? {
          subtotal: Number(bd.subtotal) || 0,
          convenienceFee: Number(bd.convenienceFee) || 0,
          flightDiscount: Number(bd.flightDiscount) || 0,
          couponDiscount: Number(bd.couponDiscount) || 0,
        }
      : null,
    // null when the applied code did not survive re-validation for THIS buyer.
    couponApplied: body.couponApplied || null,
    message: body.message || '',
  };
}

/**
 * The total-changed 409, or null.
 *
 * Sibling of cartChangeInfo above, for the part of the price the cart guard
 * cannot see. CART_CHANGED is raised on the ticket + ancillary SUBTOTAL, before
 * the server computes the convenience fee, the auto discount, the coupon and the
 * insurance premium — so a divergence in any of THOSE used to be logged and then
 * charged anyway. The case that surfaced it: /flight-offers had no optionalAuth,
 * so this page previewed retail's fee/discount/coupon to a corporate buyer while
 * the server priced the booking as B2B and applied none of them.
 *
 * The page sends `quotedTotal` (the grand total on screen) and the server stops
 * if its own figure differs by more than Rs.1. Continuing re-posts with
 * `acceptChanges`, which waives the stop and never the pricing.
 *   { newTotal, previousTotal, breakdown, couponApplied, message }
 * `couponApplied` is null when the applied code did not survive re-validation for
 * THIS buyer — the page clears it so the retry cannot re-advertise it.
 */
export function totalChangeInfo(e) {
  const body = e?.response?.data || e?.body || e;
  if (body?.code !== TOTAL_CHANGED) return null;
  return {
    newTotal: Number(body.newTotal) || 0,
    previousTotal: Number(body.previousTotal) || 0,
    breakdown: body.breakdown || null,
    couponApplied: body.couponApplied || null,
    message: body.message || '',
  };
}

/**
 * The repriced-fare 409, or null. Returns the server's new figures so the SPA can
 * show the new price and offer "continue at the new price":
 *   { newTicketAmount, newSubtotal, newTotal, previousTotal, breakdown,
 *     couponApplied, message }
 *
 * `newTotal` is a GRAND total (fee added, discounts applied), excluding only the
 * insurance premium — the one component the server will not price on a path that
 * is about to stop, since doing so mints a policy id. It used to be the ticket +
 * ancillary SUBTOTAL, and this page reconstructed the grand total by adding its
 * own displayed fee and discount, so the screen and the API disagreed about the
 * number the customer was consenting to.
 *
 * `newSubtotal` is what the retry must send back as `amount` — handing the server
 * the grand total there would give it a subtotal already inflated by its own
 * convenience fee. `breakdown` carries the server's own line items;
 * `couponApplied` is null when the applied code did not survive re-validation for
 * THIS buyer. Same contract as cartChangeInfo above.
 */
export function priceChangeInfo(e) {
  const body = e?.response?.data || e?.body || e;
  if (body?.code !== PRICE_CHANGED) return null;
  const newTicketAmount = Number(body.newTicketAmount) || 0;
  return {
    newTicketAmount,
    // Absent from a backend older than 2026-09-16, where `newTotal` WAS the
    // subtotal — fall back to it so the retry still sends a subtotal as `amount`.
    newSubtotal: Number(body.newSubtotal) || Number(body.newTotal) || 0,
    newTotal: Number(body.newTotal) || 0,
    previousTotal: Number(body.previousTotal) || 0,
    breakdown: body.breakdown || null,
    couponApplied: body.couponApplied || null,
    message: body.message || '',
  };
}

// ---- low-level endpoint calls ---------------------------------------------

/** POST /air-search — live flight results. `searchQuery` from buildAirSearchQuery(). */
export async function searchFlights(searchQuery) {
  return post('/air-search', { searchQuery });
}

// ---- fare types (Regular / Student / Senior Citizen) -----------------------
// The order is the order searches are merged in, and so the order a fare two
// searches both returned is kept in: Regular first, because an identical fare
// from a concession search is not a concession.
export const FARE_TYPE_ORDER = ['REGULAR', 'STUDENT', 'SENIOR_CITIZEN'];

/**
 * The ticked fare types, validated and in FARE_TYPE_ORDER. Accepts the array the
 * store holds or a legacy single `fareType` string. Nothing ticked means Regular,
 * because a search always runs under at least one fare type.
 * @param {string[]|string} v
 * @returns {string[]}
 */
export function normalizeFareTypes(v) {
  const want = new Set((Array.isArray(v) ? v : [v]).map((t) => String(t || '').toUpperCase()));
  const out = FARE_TYPE_ORDER.filter((t) => want.has(t));
  return out.length ? out : ['REGULAR'];
}

/**
 * The fare types a SEARCH runs under, from the flightSearch store: `fareTypes`
 * when set, else the legacy single `fareType` (a session persisted before
 * multi-select).
 */
export const searchFareTypes = (fs) => normalizeFareTypes(
  Array.isArray(fs?.fareTypes) && fs.fareTypes.length ? fs.fareTypes : fs?.fareType,
);

// Stamp every price option of one response with the search that minted it and
// the fare type it was searched under. In place: the stamps are plain strings, so
// they survive React Query's structural sharing, and a later read of the same
// response sees them without re-stamping.
function stampFareOrigin(resp, pft) {
  const tag = searchTagOf(resp);
  const tripInfos = searchRoot(resp)?.searchResult?.tripInfos || {};
  for (const trips of Object.values(tripInfos)) {
    for (const t of Array.isArray(trips) ? trips : []) {
      for (const o of t?.totalPriceList || []) {
        if (o && typeof o === 'object') { o._searchTag = tag; o._pft = pft; }
      }
    }
  }
  return tag;
}

/**
 * /air-search under EVERY ticked fare type. TripJack accepts one `pft` per
 * search, so this runs one search per type in parallel and concatenates their
 * trips into ONE response of the ordinary shape — every adapter downstream
 * (cards, combos, grouping by physical flight) works on it unchanged. The
 * stamps from stampFareOrigin keep each fare bookable only beside fares of its
 * own search, and `_subTags` lists those searches for the results page.
 *
 * One ticked type is one search, returned as-is (stamped), exactly as before.
 * A type whose search fails is dropped as long as another succeeded; if none
 * succeeded the first failure is thrown (or the first error body returned), so
 * the page reports it the way it always has.
 *
 * @param {object} args  buildAirSearchQuery() arguments; `fareTypes` (array) wins
 *                       over `fareType`.
 */
export async function searchFlightsByFareTypes(args) {
  const types = normalizeFareTypes(args?.fareTypes?.length ? args.fareTypes : args?.fareType);
  const settled = await Promise.allSettled(
    types.map((pft) => searchFlights(buildAirSearchQuery({ ...args, fareType: pft }))),
  );
  const ok = [];
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled' && searchRoot(r.value)?.searchResult?.tripInfos) {
      ok.push({ pft: types[i], resp: r.value });
    }
  });
  if (!ok.length) {
    const firstBody = settled.find((r) => r.status === 'fulfilled');
    if (firstBody) return firstBody.value;
    throw settled[0].reason;
  }
  const tags = ok.map(({ pft, resp }) => stampFareOrigin(resp, pft));
  if (ok.length === 1) {
    const only = ok[0].resp;
    only._subTags = tags;
    return only;
  }
  const merged = {};
  for (const { resp } of ok) {
    const tripInfos = searchRoot(resp).searchResult.tripInfos;
    for (const [key, trips] of Object.entries(tripInfos)) {
      if (!Array.isArray(trips)) continue;
      merged[key] = (merged[key] || []).concat(trips);
    }
  }
  const first = searchRoot(ok[0].resp);
  return { ...first, searchResult: { ...first.searchResult, tripInfos: merged }, _subTags: tags };
}

/**
 * The searches a results page is showing: the per-fare-type searches behind a
 * merged response, or the response itself. What alignLegsToSearch may move a
 * leg onto.
 * @returns {string[]}
 */
export function pageSearchTags(resp) {
  if (Array.isArray(resp?._subTags) && resp._subTags.length) return resp._subTags;
  const tag = searchTagOf(resp);
  return tag ? [tag] : [];
}

/** "Student fare" / "Senior Citizen fare" for a concession type; "" for Regular. */
export const concessionFareLabel = (pft) => (
  { STUDENT: 'Student fare', SENIOR_CITIZEN: 'Senior Citizen fare' }[String(pft || '').toUpperCase()] || ''
);

/**
 * POST /fare-rule — the airline's cancellation / date-change / no-show /
 * seat-chargeable policy windows for ONE fare option (`priceId` from the search).
 * Returns the raw response; call summarizeFareRule() to reduce it to the few
 * numbers a fare card shows. Never throws — a fare whose rules we can't fetch
 * simply renders the neutral "as per fare rules" line.
 */
export async function fetchFareRule(priceId) {
  if (!priceId) return null;
  try {
    return await post('/fare-rule', { id: String(priceId), flowType: 'SEARCH' });
  } catch (_) {
    return null;
  }
}

/**
 * POST /flight-baggage-options — real per-segment excess-baggage tiers for the
 * chosen fare(s), from TripJack review. `priceIds` are the selected legs'
 * priceIds. Returns { segments: [{ segmentId, from, to, fromCity, toCity,
 * airlineCode, flightNo, options: [{ code, amount, weightKg, desc }] }],
 * gstApplicable } — one entry per flight leg that sells excess baggage, plus
 * whether the fare accepts a GSTIN.
 *
 * ALWAYS re-throws on failure (2026-08-21) — this response carries `mandatory`
 * (is a seat/meal/baggage required for this fare to be ticketed?), which is
 * safety-relevant, not cosmetic. A failure here means we do NOT know whether a
 * seat is mandatory, and guessing "no" is how a business-class customer used to
 * reach payment with no seat picked. The caller blocks with FareExpiredModal
 * ("couldn't confirm this fare's requirements") on ANY failure, not only the
 * two classified ones (FARE_EXPIRED / UPFRONT_SEAT_UNAVAILABLE).
 */
export async function fetchBaggageOptions(priceIds) {
  const ids = (Array.isArray(priceIds) ? priceIds : [priceIds]).filter(Boolean);
  if (!ids.length) return { segments: [], meals: [], gstApplicable: false, paxConditions: null };
  try {
    const res = await post('/flight-baggage-options', { priceIds: ids });
    return {
      segments: Array.isArray(res?.segments) ? res.segments : [],
      // Per-segment meal menu from the SAME review (sI[].ssrInfo.MEAL). Older
      // backends omit it, so an absent field degrades to "no meal panel" rather
      // than breaking the baggage panel that ships in the same response.
      meals: Array.isArray(res?.meals) ? res.meals : [],
      gstApplicable: Boolean(res?.gstApplicable),
      // Is a GSTIN COMPULSORY on the reviewed fare — a different question from
      // gstApplicable ("may we collect one", true on most fares). Owned by the
      // server (utils/gstMandate.js) because only the review knows the fare that
      // will really be booked. `null`, not false, on an older backend that does
      // not send it: false would mean "the server says no" and would switch the
      // requirement OFF, so the caller falls back to its own per-leg test instead.
      gstMandatory: typeof res?.gstMandatory === 'boolean' ? res.gstMandatory : null,
      // Which leg(s) demand it — [{ from, to, airlineName, fareLabel }].
      gstMandatoryLegs: Array.isArray(res?.gstMandatoryLegs) ? res.gstMandatoryLegs : [],
      // TripJack's own passenger-form flags for THIS fare (review conditions):
      // { passportAllowed, passportRequired, passportExpiryRequired,
      //   idAllowed, idRequired, dobRequired: {adult,child,infant},
      //   ffAirlines: [] }. null → unknown (endpoint older / review failed) — the
      //   form falls back to geography, and to the searched fare type for the ID.
      // idAllowed/idRequired are the CONCESSION ID CARD (Student / Senior Citizen,
      // review conditions.dc) and are deliberately separate from the passport
      // flags: the two are different documents and reading dc as a passport
      // requirement is what once made a passport compulsory on a student fare.
      paxConditions: res?.paxConditions || null,
      // Fares that will NOT be ticketed unless the add-on is chosen (review's
      // ifsm/ifmm/ifbm, plus the newer per-trip `ism` for IndiGo Upfront). The
      // airline rejects the book with "Seat Selection is Mandatory" AFTER
      // payment, so the form has to insist up front.
      mandatory: res?.mandatory || { seat: false, meal: false, baggage: false },
      // WHICH sectors demand a seat, by TripJack segment id. A return trip can
      // have one Upfront leg and one ordinary leg, so this is not all-or-nothing.
      // Empty on an older backend → the form falls back to "every sector", which
      // is exactly what it did before this field existed.
      seatMandatorySegmentIds: Array.isArray(res?.seatMandatorySegmentIds)
        ? res.seatMandatorySegmentIds.map(String)
        : [],
      // The allowance the reviewed fare INCLUDES, per leg group —
      // [{ tripIndex, from, to, segmentIds, checkIn, cabin }]. Distinct from
      // `segments` above (excess baggage for SALE). An airline can change it
      // between the search and the review, so FlightBook diffs it against what
      // the fare card advertised. Empty on an older backend, which reads as "no
      // change to report" — exactly the behaviour before this field existed.
      freeBaggage: Array.isArray(res?.freeBaggage) ? res.freeBaggage : [],
    };
  } catch (e) {
    // ALWAYS re-throw — never invent a "nothing required" answer. This used to
    // swallow anything except FARE_EXPIRED / UPFRONT_SEAT_UNAVAILABLE and return
    // a fallback with no `mandatory` key at all, which FlightBook read as
    // `mandatorySeat = false`: a review that failed for an unrelated reason
    // (a transient 500, a margin-rule crash, a timeout) was silently treated as
    // "this fare needs no seat" instead of "we don't know yet". On a seat-
    // mandatory fare that let the customer complete the whole form with no seat
    // picked, reach payment, and only then hit the "seat required" block — on a
    // scarce-inventory cabin (business class) that block can arrive AFTER the
    // seat that was available at the earlier check has since been taken, i.e.
    // exactly the "paid then failed" case this fare-review machinery exists to
    // prevent. The caller (FlightBook) now treats any failure here as "couldn't
    // confirm this fare's requirements" and blocks with FareExpiredModal instead
    // of guessing.
    throw e;
  }
}

/**
 * GET /airports?query= — city/airport autocomplete (DB-backed, Indian airports
 * first). Returns an array of { code, name, city, country }. Normalised so the
 * UI always gets those four fields regardless of column casing.
 */
export async function searchAirports(query) {
  const q = String(query || '').trim();
  if (!q) return [];
  const rows = await get(`/airports?query=${encodeURIComponent(q)}`);
  return (Array.isArray(rows) ? rows : []).map((r) => ({
    code: String(r.code || r.iata || r.iataCode || '').toUpperCase(),
    name: r.name || r.airport || r.airportName || '',
    city: r.city || r.cityName || '',
    country: r.country || r.countryName || '',
  })).filter((a) => a.code);
}

/**
 * Resolve IATA codes to their EXACT `airports` rows, keyed by code.
 *
 * The search endpoint is a LIKE across code/name/city/country, so querying "DEL"
 * also returns every row merely CONTAINING it — we keep only the exact code match.
 * Used to label a default/seeded airport from the database instead of a bundled
 * constant: the hand-written list had drifted from the table (it called DEL
 * "New Delhi"; the row says "Delhi").
 *
 * Never throws — an offline or failing lookup resolves to `{}` and the caller keeps
 * whatever label it already had.
 *
 * @param {string[]} codes
 * @returns {Promise<Record<string, {code, name, city, country}>>}
 */
export async function lookupAirportsByCode(codes) {
  const wanted = [...new Set((codes || [])
    .map((c) => String(c || '').trim().toUpperCase())
    .filter((c) => /^[A-Z]{3}$/.test(c)))];
  if (!wanted.length) return {};
  const found = {};
  await Promise.all(wanted.map(async (code) => {
    try {
      const rows = await searchAirports(code);
      const exact = rows.find((r) => r.code === code);
      if (exact) found[code] = exact;
    } catch {
      // Leave the code unresolved; the caller falls back to its bundled label.
    }
  }));
  return found;
}

/**
 * POST /fare-rule — fare rules (cancellation / date-change / no-show fees) for a
 * priceId. `flowType` is 'SEARCH' (from results) or 'REVIEW'. Returns the raw
 * TripJack fareRule payload; the FareRulePanel parses it.
 */
export async function fareRule(id, flowType = 'SEARCH') {
  if (!id) return null;
  return post('/fare-rule', { id, flowType });
}

/**
 * GET /flight-offers — checkout Price-Summary / Offers panel data: the active
 * convenience fee, any auto flight-discount rules and the visible coupon codes.
 * Display-only (the money charged is recomputed server-side at initiate). Never
 * throws — returns empties so the panel degrades quietly.
 */
export async function fetchFlightOffers() {
  try {
    const res = await get('/flight-offers');
    return {
      convenienceFee: res?.convenienceFee || null,
      // Every candidate fee rule, newest first — which one applies depends on
      // the fare chosen (lib/fareBreakdown.js pickConvenienceFee). Absent from
      // an older backend, which is why it is not defaulted to [].
      ...(Array.isArray(res?.convenienceFees) ? { convenienceFees: res.convenienceFees } : {}),
      flightDiscounts: Array.isArray(res?.flightDiscounts) ? res.flightDiscounts : [],
      coupons: Array.isArray(res?.coupons) ? res.coupons : [],
    };
  } catch (_) {
    return { convenienceFee: null, flightDiscounts: [], coupons: [] };
  }
}

/**
 * GET /check-coupon/:code — validate a promo code the customer typed (supports
 * codes not in the visible list). Public (optionalAuth): usable from the step-1
 * search-results promo box before login, same as /flight-offers / /air-search;
 * a signed-in session still narrows to the right b2c/b2b audience scope.
 *
 * Returns { row, reason }. `row` is the coupon { code, discount_type,
 * discount_value, ... } on success, else null. `reason` is one of
 * 'invalid' | 'expired' | 'exhausted' | 'scope' | 'network' on failure — lets
 * the caller show a SPECIFIC reason instead of one generic "invalid" message
 * (CR B1). Never throws.
 *
 * `legs` (lib/fareBreakdown.js tripLegsFor) — the trip chosen so far. A code an
 * admin scoped to an airline or a fare family is refused with reason 'fare' when
 * the trip is not on it, so the customer hears why here rather than having the
 * discount vanish at payment. Omitted (no fare chosen yet) the code is judged on
 * everything else; initiate judges it against the reviewed fare regardless.
 */
export async function checkFlightCoupon(code, legs = null) {
  const c = String(code || '').trim();
  if (!c) return { row: null, reason: null };
  try {
    // `product` scopes the coupon: a hotel-only code must not validate here. The
    // server treats a MISSING product as "'all' coupons only", so this is not
    // optional — omit it and every flight-scoped coupon silently stops working.
    const res = await get(`/check-coupon/${encodeURIComponent(c)}?product=flight`);
    const row = res?.success && Array.isArray(res.data) ? res.data[0] : null;
    if (row && Array.isArray(legs) && legs.length && !ruleFitsTrip(row, legs)) {
      // Name the half a Domestic/International code belongs to; anything else
      // it refuses on is the airline or the fare type.
      const want = domIntMiss(row, legs);
      return { row: null, reason: want ? `${want}_only` : 'fare' };
    }
    return { row: row || null, reason: row ? null : (res?.reason || 'invalid') };
  } catch (_) {
    return { row: null, reason: 'network' };
  }
}

/**
 * POST /flight-payment/initiate — start an Easebuzz hosted-checkout payment.
 * The backend re-prices the fare (server-side /air-review → bookingId), stashes
 * the traveller context and returns { success, txnid, paymentUrl }. The caller
 * then redirects the browser to `paymentUrl`; Easebuzz posts the result back to
 * the backend, which books the flight and redirects to /confirmation.
 */
export async function initiateFlightPayment(body) {
  return post('/flight-payment/initiate', body);
}

/**
 * Zero-gateway settle: when the wallet covers the whole total, initiate returns
 * { walletSettle:true, txnid } instead of a gateway URL. This places the booking
 * server-side (capturing the wallet hold) and returns { redirect } to navigate to.
 * Authenticated — the Bearer token is auto-attached by the api client.
 */
export async function flightWalletSettle(body) {
  return post('/flight-payment/wallet-settle', body);
}

/**
 * POST /flight-seat-map — live seat map for the chosen fare. The backend reviews
 * the priceIds → bookingId → TripJack seat map, and returns the normalised
 * per-leg grid: { segments:[{ key, label, seats:[{ id,row,col,booked,price,
 * legroom,aisle,window }] }] }. Empty segments → no seat map for that flight.
 */
export async function fetchSeatMap(priceIds) {
  const ids = (Array.isArray(priceIds) ? priceIds : [priceIds]).filter(Boolean);
  if (!ids.length) return { segments: [] };
  return post('/flight-seat-map', { priceIds: ids });
}

/**
 * POST /flight-meal/options — live meal SSR for a TICKETED booking. Returns
 * { success, meals:[{ key, code, desc, amount, segmentKey }], segments, error }.
 * meals is [] when the airline offers no meal for that PNR.
 */
export async function fetchSsrOptions(bookingId) {
  if (!bookingId) return { success: false, segments: [], seatSegments: [] };
  return post('/flight-ssr/options', { bookingId });
}

/**
 * POST /flight-ssr/initiate — pay for the chosen add-ons.
 *
 * `selections` carries IDENTITY ONLY — [{ segmentId, travellerId, type, code }].
 * The backend re-fetches the booking's live options and reads every price off
 * the supplier's own rows, so a tampered client cannot buy a ₹15,000 bag for ₹1.
 * Returns { success, txnid, amount, paymentUrl }; the caller redirects to
 * `paymentUrl`.
 */
export async function initiateSsrPayment(body) {
  return post('/flight-ssr/initiate', body);
}

/**
 * POST /flight-reissue/options — RESCHEDULE. With no `date` it returns just the
 * booking (PNR, passengers, current flight) so the picker can render; with a date
 * it also returns the flights it can move to, each carrying the fare DIFFERENCE
 * itemised (base diff, tax diff, airline + TripJack reissue fees, payable).
 */
export async function fetchReissueOptions(ref, date, paxIds) {
  if (!ref) return { success: false, flights: [] };
  return post('/flight-reissue/options', { ref, date: date || '', paxIds: paxIds || null });
}

/**
 * POST /flight-reissue/initiate — pay the fare difference and reschedule.
 * The browser sends only the chosen `priceId`; the server revalidates with the
 * airline and charges the supplier's own figure, never a client-supplied one.
 */
export async function initiateReissuePayment(body) {
  return post('/flight-reissue/initiate', body);
}

// ---- request builder -------------------------------------------------------

/** Build the /air-search `searchQuery`. One-way today; routeInfos is an array
 *  so return/multi-city extend it later without touching this contract. */
export function buildAirSearchQuery({
  from,
  to,
  departDate,
  returnDate,                  // set → adds the return leg (round trip)
  legs,                        // [{ from, to, date }] → multi-city routeInfos
  cabinClass = 'ECONOMY',
  adults = 1,
  children = 0,
  infants = 0,
  direct = false,
  fareType = 'REGULAR',       // REGULAR | STUDENT | SENIOR_CITIZEN
  preferredAirlines = [],      // [] = any, else array of IATA codes
}) {
  const route = (f, t, d) => ({
    fromCityOrAirport: { code: f },
    toCityOrAirport: { code: t },
    travelDate: d,
  });
  let routeInfos;
  if (Array.isArray(legs) && legs.length) {
    // multi-city — fall back to the search departDate if a leg has no date set,
    // so TripJack never receives an empty travelDate (it 400s on that).
    routeInfos = legs.map((l) => route(l.from, l.to, l.date || departDate));
  } else if (returnDate) {
    routeInfos = [route(from, to, departDate), route(to, from, returnDate)]; // round trip
  } else {
    routeInfos = [route(from, to, departDate)];                            // one-way
  }
  // searchModifiers must be a non-empty object (searchAirValidator). pft is the
  // TripJack passenger fare type; prefAirline narrows to the chosen carriers.
  const searchModifiers = {
    isDirectFlight: Boolean(direct),
    isConnectingFlight: false,
    pft: fareType || 'REGULAR',
  };
  const airlines = (Array.isArray(preferredAirlines) ? preferredAirlines : [])
    .filter(Boolean);
  if (airlines.length) searchModifiers.prefAirline = airlines;

  return {
    cabinClass,
    paxInfo: {
      ADULT: Number(adults) || 1,
      CHILD: Number(children) || 0,
      INFANT: Number(infants) || 0,
    },
    routeInfos,
    searchModifiers,
  };
}

// ---- helpers ---------------------------------------------------------------

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Brand colour + 2-char badge per airline IATA code (matches the design palette).
// UK and G8 stay in this map on purpose even though we no longer SELL either
// (see RETIRED_AIRLINES below) — a booking made before they were retired still
// has to render its airline on the details page and the voucher.
const AIRLINE_BRAND = {
  AI: { color: '#b91c1c', logo: 'AI' },
  IX: { color: '#ea580c', logo: 'IX' },
  '6E': { color: '#1e3a8a', logo: '6E' },
  SG: { color: '#dc2626', logo: 'SG' },
  UK: { color: '#4c1d95', logo: 'UK' },
  QP: { color: '#7c3aed', logo: 'QP' },
  G8: { color: '#db2777', logo: 'G8' },
};
function brandFor(code) {
  return AIRLINE_BRAND[code] || { color: '#475569', logo: (code || '··').slice(0, 2) };
}

// Carriers we no longer sell. Vistara (UK) was merged into Air India and Go
// First (G8) ceased operations, so the supplier should not be returning either
// — this is the safety net for stale inventory. Dropping the trip here, at the
// one place a search response becomes flight cards, keeps them out of the
// results list, the sidebar airline facet and the booking flow at once, rather
// than each of those needing its own check.
//
// Deliberately NOT applied to AIRLINE_BRAND or the /airlines logo set: an
// already-booked Vistara ticket still has to render its colour and mark on the
// booking-details page and the voucher.
const RETIRED_AIRLINES = new Set(['UK', 'G8']);

/** True when any segment of a trip is flown by a carrier we no longer sell. */
function hasRetiredCarrier(trip) {
  return (trip?.sI || []).some(
    (s) => RETIRED_AIRLINES.has(String(s?.fD?.aI?.code || '').toUpperCase()),
  );
}

// TripJack times are ISO-ish local strings, e.g. "2026-06-24T23:25" (no zone).
function timeOf(iso) {
  if (!iso) return '';
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}
function dateKey(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[0] : '';
}
function minutesBetween(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  const diff = (db - da) / 60000;
  return Number.isFinite(diff) && diff > 0 ? Math.round(diff) : 0;
}
function durLabel(mins) {
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
}
function dayDiff(a, b) {
  const ka = dateKey(a);
  const kb = dateKey(b);
  if (!ka || !kb) return 0;
  return Math.round((new Date(kb) - new Date(ka)) / 86400000);
}
// Whole-trip elapsed time. NOT minutesBetween(firstDep, lastArr) — TripJack's
// dt/at are zone-less local strings, so diffing the two ends directly loses
// the origin/destination UTC-offset gap whenever they're in different zones.
// Each segment's own `duration` is already correct (supplier-computed, same
// field the expanded Flight Information panel uses); layover gaps ARE safe
// to diff naively since both of a layover's timestamps are the same airport.
function tripDurationMinutes(segs) {
  if (!segs.length) return 0;
  let total = 0;
  for (let i = 0; i < segs.length; i += 1) {
    const s = segs[i];
    total += s.duration ?? minutesBetween(s.dt, s.at);
    if (i < segs.length - 1) total += minutesBetween(s.at, segs[i + 1].dt);
  }
  return total;
}

/**
 * TripJack's OFFER families (`OFFER`, `OFFER_FARE_WITH_PNR`) are sold as this
 * storefront's OWN exclusive fare, so this is the name the customer is shown in
 * place of the supplier's — the fare TYPE, not a badge next to it. "Offer fare
 * with pnr" describes how the airline holds the seat; it is not a product a
 * customer can be asked to choose between.
 *
 * This build IS one storefront, so it names itself (the same way ShareMenu and
 * WhatsAppButton do). Not a brand CODE (CLAUDE.md §6) — nothing here selects
 * data, it is the product name printed on screen.
 */
export const EXCLUSIVE_FARE_LABEL = 'Neon Exclusive Fare';

const FARE_LABEL = {
  PUBLISHED: 'Published',
  SME: 'SME',
  COUPONFARE: 'Coupon',
  CORPORATE: 'Corporate',
  CORPORATE_FARE: 'Corporate',
  FLEXI: 'Flexi',
  TACTICAL: 'Saver',
  SPECIAL_RETURN: 'Special Return',
};
function fareName(opt) {
  // `fareIdentifer` (sic) is TripJack's own misspelling of the same field, seen
  // alongside the correct key — the backend's fareTypeOf() has always read both,
  // and reading only one labels a fare that is not Published as "Published".
  const id = String(opt?.fareIdentifier || opt?.fareIdentifer || '').toUpperCase();
  if (!id) return 'Published';
  // An OFFER fare is sold as THIS storefront's own exclusive fare, so it is named
  // as one wherever a fare type is printed — see fareTypeLabel.
  if (id.startsWith('OFFER')) return EXCLUSIVE_FARE_LABEL;
  // A supplier enum is not a label. The unmapped fallback used to print the raw
  // underscore — a live e-ticket read "Economy · Corporate_fare" — which is why
  // SPECIAL_RETURN had to be hand-mapped to stop it saying "Special_return". The
  // map should not need a new row every time TripJack adds an underscore.
  return FARE_LABEL[id] || (id[0] + id.slice(1).toLowerCase()).replace(/_/g, ' ');
}

/**
 * The fare type as the CUSTOMER is shown it, given a name that was derived
 * elsewhere — the server's persisted `leg.fareType.fareName` on a voucher or a
 * reopened booking, where the supplier's own wording ("Offer", "Offer fare with
 * pnr") would otherwise reach the screen. One mapping, so the fare type a
 * customer chooses in the popup is the fare type their voucher names.
 *
 * @param {string} raw  a display name (fareName()'s output, or the backend's).
 */
export const fareTypeLabel = (raw) => {
  const s = String(raw || '').trim();
  return /^offer\b/i.test(s) || corpNorm(s).startsWith('OFFER') ? EXCLUSIVE_FARE_LABEL : s;
};

// ── Special Return pairing (round trip) ───────────────────────────────────────
// SPECIAL_RETURN is one airline fare for the whole round trip, sold as two halves.
// Verified live on TripJack UAT 2026-09-14 (DEL-BOM, BOM-DEL). Every refusal was
// errCode 1080 "All Segments Must be selected if Special Return fare.":
//   Special + ordinary fare .............................................. refused
//   Special (AI) + Special (6E) .......................................... refused
//   Special (6E) + Special (6E) — 6E sends no sri, msri [] ................ accepted
//   Special (AI) + Special (AI), return msri lists onward sri ............ accepted
//   Special (AI) + Special (AI), SAME two flights, codes don't match ..... refused
// So "same airline" is not enough when the airline sends pairing codes: the return
// option's `msri` must list the onward option's `sri` (AI also lists it the other
// way round, which is accepted as well). No codes at all → the airline check is
// the whole rule.
// ── Corporate fare ────────────────────────────────────────────────────────────
// A CORPORATE fare is sold to a registered business and invoiced against its
// GSTIN, so the booking form makes GST mandatory on it. Deciding "is this a
// corporate fare?" is easy to get wrong in two ways, and getting it wrong lets a
// corporate ticket be issued with no GSTIN — the one thing the fare exists to
// carry — so the test lives HERE, once, instead of being re-typed per page:
//
//   1. TripJack returns BOTH `CORPORATE` and `CORPORATE_FARE` for the same
//      category. FARE_LABEL above maps both, and so does the backend's own
//      FARE_LABEL ("TripJack returns BOTH spellings of the corporate category").
//      An exact `=== 'CORPORATE'` therefore misses a real corporate fare.
//   2. TripJack ships `fareIdentifer` (sic) alongside the correct key — the same
//      misspelling fareName(), the backend's fareTypeOf() and marginRuleService
//      all read. Reading only the correct key leaves the identifier empty.
//
// Falls back to the fare's DISPLAY NAME only when no identifier is present at all
// (a selection persisted before fareIdentifier was carried through), which is why
// the comparison is punctuation-insensitive: the label is "Corporate" when mapped
// and "Corporate fare" when it came through the unmapped title-case path.
const corpNorm = (v) => String(v || '').trim().toUpperCase().replace(/[^A-Z]/g, '');
export const isCorporateFare = (leg) => {
  const id = corpNorm(leg?.fareIdentifier || leg?.fareIdentifer);
  const probe = id || corpNorm(leg?.fareName);
  return probe === 'CORPORATE' || probe === 'CORPORATEFARE';
};

/** An SME fare — TripJack's `SME`, the other fare family sold to a business. */
export const isSmeFare = (leg) => {
  const id = corpNorm(leg?.fareIdentifier || leg?.fareIdentifer);
  const probe = id || corpNorm(leg?.fareName);
  return probe === 'SME' || probe === 'SMEFARE';
};

// ── Mandatory GST ─────────────────────────────────────────────────────────────
// A GSTIN is COMPULSORY on an IndiGo (6E) CORPORATE or SME fare, and on nothing
// else (client decision 2026-09-22). Both axes have to hold: IndiGo's published
// fare needs none, and another carrier's corporate fare needs none either.
//
// The rule that shipped before this was `isCorporateFare` alone, which was wrong
// in both directions — it blocked an Air India corporate fare behind a GSTIN that
// airline never asks for, and did not recognise an SME fare at all, so IndiGo SME
// reached the gateway with an empty GST block and was refused by the airline at
// BOOK, after the customer had paid.
//
// The SERVER owns this rule (booking_backend/utils/gstMandate.js) and answers it
// on the review — `gstMandatory` off fetchBaggageOptions — because only the review
// knows the fare that will really be booked. What follows is the FALLBACK for a
// backend that does not send it yet, computed off the chosen legs' own supplier
// values, plus the pre-payment 409 GST_REQUIRED as the boundary behind both.
const GST_MANDATORY_AIRLINES = new Set(['6E']);

// The marketing carrier of a chosen leg. `_raw.sI` is the supplier's own itinerary
// (persisted with the selection); `code` is the card's "6E2004" — airline code
// then flight number — and an IATA airline code is always its first two
// characters, so it is a safe fallback for a selection restored from
// sessionStorage before `_raw` was carried through.
export const airlineCodeOf = (leg) => {
  const raw = leg?._raw?.sI?.[0]?.fD?.aI?.code || leg?.airlineCode || '';
  if (raw) return String(raw).trim().toUpperCase();
  return String(leg?.code || '').trim().toUpperCase().slice(0, 2);
};

/** Does THIS leg demand a GSTIN? Airline AND fare family, never one alone. */
export const isGstMandatoryFare = (leg) =>
  GST_MANDATORY_AIRLINES.has(airlineCodeOf(leg)) && (isCorporateFare(leg) || isSmeFare(leg));

/**
 * The GST mandate for a whole trip: `required` when ANY leg demands one (a single
 * gstInfo block is sent for the order), plus the label of the fare family and the
 * airline that caused it, so the notice can name them instead of always saying
 * "Corporate". `serverSaid` — gstMandatory off the review — wins when present;
 * the per-leg test is the fallback for an older backend.
 */
export const gstMandateFor = (legs, serverSaid = null) => {
  const list = (Array.isArray(legs) ? legs : [legs]).filter(Boolean);
  const hit = list.find(isGstMandatoryFare) || null;
  const required = serverSaid == null ? Boolean(hit) : Boolean(serverSaid);
  return {
    required,
    // Falls back to the generic words when the server mandated a fare the local
    // test could not classify — the requirement still stands, the copy just stops
    // naming a family it is not sure of.
    fareLabel: hit ? (isSmeFare(hit) ? 'SME' : 'Corporate') : 'corporate',
    airline: hit?.airline || '',
  };
};

/**
 * An OFFER fare — TripJack's `OFFER`, and `OFFER_FARE_WITH_PNR` for the variant
 * that books against a held PNR. Matched on the PREFIX rather than an exact enum
 * for the same reason isCorporateFare() normalises punctuation: the supplier ships
 * more than one spelling of a category, and an exact test silently stops
 * recognising the fare the day a new one appears. These are sold as the
 * storefront's own exclusive fare, and carry booking conditions the customer has
 * to see before paying — see the note in FareOptionsModal.
 */
export const isExclusiveFare = (fare) =>
  corpNorm(fare?.fareIdentifier || fare?.fareIdentifer).startsWith('OFFER');

export const isSpecialReturn = (fare) =>
  String(fare?.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN';

// TripJack's minimum gap between one leg's arrival and the next leg's departure.
// Its own results page enforces it ("Departure time must be greater than previous
// Arrival time by 2hours") and its review refuses a pair that breaks it.
export const MIN_LEG_GAP_MINUTES = 120;

/**
 * Does `next` leave too soon after `prev` lands?
 * Both times are TripJack's zone-less LOCAL strings (dt/at), compared as-is. For a
 * round trip both are at the same airport (or one city), so they share a clock.
 * Signed on purpose — minutesBetween() clamps to 0, and a return that departs
 * BEFORE the onward lands is the worst case, not a zero.
 * @param {object|null} prev  flight card (toFlightCard) of the earlier leg.
 * @param {object|null} next  flight card of the following leg.
 * @returns {null|{gapMin:number, arr:string, dep:string}} null when fine, or when
 *          either leg is missing / has no usable time (nothing to judge).
 */
export function legGapIssue(prev, next) {
  if (!prev || !next) return null;
  const a = Date.parse(prev.arrISO);
  const d = Date.parse(next.depISO);
  if (!Number.isFinite(a) || !Number.isFinite(d)) return null;
  const gapMin = Math.round((d - a) / 60000);
  if (gapMin >= MIN_LEG_GAP_MINUTES) return null;
  return { gapMin, arr: prev.arr || timeOf(prev.arrISO), dep: next.dep || timeOf(next.depISO) };
}

/** Customer-facing line for a legGapIssue() verdict. */
export function legGapMessage(issue, prevLabel = 'onward', nextLabel = 'return') {
  if (!issue) return '';
  const when = issue.gapMin <= 0
    ? `leaves before your ${prevLabel} flight lands`
    : `leaves only ${issue.gapMin < 60 ? `${issue.gapMin} min` : durLabel(issue.gapMin)} after your ${prevLabel} flight lands`;
  return `Your ${nextLabel} flight (${issue.dep}) ${when} (${issue.arr}). Airlines need at least 2 hours between connecting flights — please choose a later ${nextLabel} flight.`;
}

/**
 * Why a round trip's onward + return fares cannot be sold together.
 * @param {object|null} onward  fare object (faresOf) chosen on the onward leg.
 * @param {object|null} ret     fare object chosen on the return leg.
 * @returns {null|'BOTH_SPECIAL'|'SAME_AIRLINE'|'NOT_PAIRED'} null when bookable
 *          (or when a leg has no fare yet — nothing to judge).
 */
export function specialReturnIssue(onward, ret) {
  if (!onward || !ret) return null;
  const a = isSpecialReturn(onward);
  const b = isSpecialReturn(ret);
  if (!a && !b) return null;
  if (a !== b) return 'BOTH_SPECIAL';
  if ((onward.airlineCode || '') !== (ret.airlineCode || '')) return 'SAME_AIRLINE';
  const lists = (list, code) =>
    Array.isArray(list) && code != null && code !== '' && list.map(String).includes(String(code));
  const coded = (onward.sri && Array.isArray(ret.msri) && ret.msri.length)
    || (ret.sri && Array.isArray(onward.msri) && onward.msri.length);
  if (!coded) return null;
  return lists(ret.msri, onward.sri) || lists(onward.msri, ret.sri) ? null : 'NOT_PAIRED';
}

/**
 * The fares of ONE leg that can still be sold alongside `anchor` — the fare
 * already chosen on an EARLIER leg of the same trip.
 *
 * This is specialReturnIssue() turned round: instead of judging a finished pair
 * and explaining the refusal afterwards, it narrows what the later leg is even
 * offered, so the unbookable mix is never on screen to be chosen. The popup
 * stays as the backstop (a leg can still be changed from the fare modal), but on
 * a normal pass the customer simply never meets it.
 *
 * Two rules, in this order:
 *   1. FAMILY — a Special Return fare is half of one round-trip fare, so it
 *      pairs only with another Special Return fare, and an ordinary fare only
 *      with an ordinary one. This rule is unconditional and cuts both ways: it
 *      is what hides the Special Return fares once the first leg is on Published
 *      / Eco / anything else.
 *   2. PAIRING — two Special Return fares must also be the same airline, and
 *      must match on the airline's own sri/msri codes when it sends them (see
 *      specialReturnIssue). `strict: false` applies the family rule only, for
 *      trips where the pairing codes are not verified to be meaningful.
 *
 * Returns the ORIGINAL array (same reference) when nothing is excluded, so a
 * caller can cheaply tell "narrowed" from "unchanged", and [] when this leg
 * sells nothing that pairs — which is a real answer the caller must handle, not
 * a reason to fall back to showing an unbookable fare.
 *
 * @param {object[]} fares   one leg's fare options (faresOf).
 * @param {object|null} anchor  the fare chosen on an earlier leg; null = no lock.
 * @param {{strict?: boolean}} [opts]
 * @returns {object[]} the subset that pairs with `anchor`.
 */
export function pairableFares(fares, anchor, opts = {}) {
  const list = Array.isArray(fares) ? fares : [];
  if (!anchor || !list.length) return list;
  const wantSpecial = isSpecialReturn(anchor);
  const family = list.filter((f) => isSpecialReturn(f) === wantSpecial);
  // An ordinary anchor is done at the family rule: sri/msri only ever describe
  // how two SPECIAL fares pair, and specialReturnIssue() returns null for two
  // ordinary fares regardless of airline — a round trip on two carriers is a
  // perfectly ordinary booking.
  if (!wantSpecial || opts.strict === false) return family.length === list.length ? list : family;
  const paired = family.filter((f) => specialReturnIssue(anchor, f) === null);
  return paired.length === list.length ? list : paired;
}

// Pretty-print a TripJack baggage allowance. `bI` (baggageInfo) carries `iB`
// (included check-in, e.g. "15 KG" or "1 Piece") and `cB` (cabin, e.g. "7 KG").
function baggageLine(raw, fallback) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return fallback;
  // A bare number means kilograms.
  if (/^\d+$/.test(s)) return `${s} Kgs`;
  return s;
}
/**
 * NEVER invent an allowance. This used to default to "15 Kgs Check-in Baggage" /
 * "7 Kgs Cabin Baggage" when the supplier said nothing — so a fare that includes NO
 * checked bag (common on IX/6E lite fares) was advertised as including 15 kg, with a
 * green tick, and the customer found out at the airport. Absent means absent: null,
 * which every consumer already renders honestly ("as per fare", "—", "NA").
 */
function baggageOf(adultBucket) {
  const bI = adultBucket?.bI || {};
  return {
    checkIn: baggageLine(bI.iB, null),
    cabin: baggageLine(bI.cB, null),
  };
}

// The order TripJack's own Baggage Information panel lists pax types in, and the
// words it uses. Anything the supplier returns outside this set is appended in
// response order rather than dropped.
const PAX_ORDER = ['ADULT', 'CHILD', 'INFANT'];
const PAX_LABEL = { ADULT: 'Adult', CHILD: 'Child', INFANT: 'Infant' };

/**
 * The allowance PER PASSENGER TYPE — the grid TripJack shows (Adult 15/7,
 * Child 15/7, Infant 0/7).
 *
 * Every figure here was already in hand: TripJack quotes baggage per pax bucket
 * at `fd.<PAX>.bI`, and optionTotals already walks those same buckets for the
 * Fare Details table. We simply collapsed them — `baggageOf(adult)` above took
 * ONE bucket, so the Baggage Information tab repeated the adult row per SEGMENT
 * while the real variation is per PAX TYPE. An infant's checked allowance is
 * routinely 0 Kg where the adult's is 15 Kg, and we were printing the adult's.
 *
 * NOT filtered by the searched pax counts — deliberately, to match TripJack:
 * the tab describes what the FARE carries, which a customer wants to read
 * before deciding who to add to the booking. (The Fare Details tab is the one
 * that is count-driven, because that one is about money actually owed.)
 *
 * A bucket quoting neither figure is dropped rather than rendered blank, on the
 * same principle as baggageOf: absent means the supplier said nothing.
 */
function baggageByPaxOf(fd) {
  const buckets = Object.entries(fd || {});
  const rank = (t) => {
    const i = PAX_ORDER.indexOf(String(t).toUpperCase());
    return i === -1 ? PAX_ORDER.length : i;
  };
  return buckets
    .map(([type, bucket]) => {
      const bI = bucket?.bI || {};
      const checkIn = baggageLine(bI.iB, null);
      const cabin = baggageLine(bI.cB, null);
      if (!checkIn && !cabin) return null;
      const key = String(type).toUpperCase();
      return { type: key, label: PAX_LABEL[key] || key, checkIn, cabin };
    })
    .filter(Boolean)
    .sort((a, b) => rank(a.type) - rank(b.type));
}

// Round to PAISE, never to rupees. TripJack's fares carry paise (a real ADULT fare
// on IX2952 is TF 2416.50 = BF 1009 + 1407.50), and Math.round() turned 1407.50 into
// 1408 BEFORE the value ever reached a formatter — so the Fare Details table showed a
// tax the airline never charged, and the rows no longer summed to the total. This is
// display shaping only: it keeps the supplier's exact figure and merely absorbs
// float artefacts (…4999999998) that plain arithmetic would surface. `inr()` already
// prints 2 decimals when a value has them, so nothing else had to change.
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Sum a price option (one totalPriceList entry) across the searched pax mix.
// Customer pays Σ FP·count; we owe TripJack Σ TF·count.
function optionTotals(opt, pax) {
  let customer = 0;
  let supplier = 0;
  let base = 0;
  let taxes = 0;
  let adultFP = 0;
  // Real per-passenger-type breakdown for the Fare Details table (no fabricated
  // percentages): base + taxes + processing (= our markup) per pax, with counts.
  const perPax = [];
  for (const [type, bucket] of Object.entries(opt?.fd || {})) {
    const fc = bucket?.fC || {};
    const count = num(pax?.[type]);
    const fp = num(fc.FP ?? fc.TF);
    const tf = num(fc.TF);
    const bf = num(fc.BF) || Math.round(tf * 0.55); // fall back to a base split
    customer += fp * count;
    supplier += tf * count;
    base += bf * count;
    taxes += (tf - bf) * count;
    if (type === 'ADULT') adultFP = fp;
    if (count > 0) {
      perPax.push({
        type,                              // ADULT | CHILD | INFANT
        count,
        base: r2(bf),                      // base fare per pax
        taxes: r2(Math.max(0, tf - bf)),   // taxes & fees per pax
        proc: r2(Math.max(0, fp - tf)),    // processing (our markup) per pax
        fare: r2(fp),                      // total customer fare per pax
      });
    }
  }
  const adult = opt?.fd?.ADULT || {};
  // rT (refundableType): 1 = Refundable, 2 = Non-refundable. null when absent so
  // the UI shows a neutral "as per fare rules" line instead of a wrong claim.
  const refundable = adult.rT === 1 ? true : adult.rT === 2 ? false : null;
  // Seats remaining, if the supplier reports it (various field names seen).
  const seats = num(opt?.sR ?? adult.sR ?? adult.sr ?? adult.seatRemaining ?? opt?.seatRemaining);
  // mI = "meal included" on the fare. true → complimentary meals; false →
  // chargeable. Absent → null (UI stays neutral rather than claiming either).
  const mealIncluded = typeof adult.mI === 'boolean' ? adult.mI : null;
  // cB = the airline's BOOKING CLASS / reservation designator for this fare
  // ("EC", "XN", …). NOTE the collision: `adult.bI.cB` one level down is the
  // CABIN BAGGAGE allowance ("7 Kg") — same key, unrelated meaning. Read the
  // wrong one and you print a weight where the RBD should be.
  const bookingClass = String(adult.cB || '').trim() || null;
  return {
    priceId: opt?.id,
    name: fareName(opt),
    cabinClass: adult.cc || '',
    bookingClass,
    refundable,
    seats,
    mealIncluded,
    baggage: baggageOf(adult),
    // Per-pax-type grid for the Baggage Information tab — see baggageByPaxOf.
    // `baggage` above stays the ADULT summary every existing consumer reads.
    baggageByPax: baggageByPaxOf(opt?.fd),
    customer: r2(customer),
    supplier: r2(supplier),
    base: r2(base),
    taxes: r2(taxes),
    adultFP: r2(adultFP),
    perPax,
    _opt: opt,
  };
}

// ---- adapters: v1 air-search → UI shapes ----------------------------------

/**
 * Map one trip (a flight option group) to the card shape FlightCard consumes,
 * plus the live booking keys (priceId / supplierPrice / fares[]) on the object.
 * `pax` is the searched { ADULT, CHILD, INFANT } mix so totals are correct.
 */
const CABIN_LABEL = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium Economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

/**
 * A stable identity for one physical flight: every segment's airline + flight
 * number + departure timestamp, joined. Two search responses (run for different
 * cabin classes) describe the SAME flight when their signatures match — a
 * connecting itinerary only matches if its whole segment chain matches.
 */
export function tripSignature(trip) {
  return (trip?.sI || [])
    .map((s) => `${s?.fD?.aI?.code || ''}${s?.fD?.fN || ''}@${s?.dt || ''}`)
    .join('|');
}

// WHICH /air-search RESPONSE a fare came from. TripJack reviews a trip's priceIds
// only when they all belong to ONE search — it counts them against that search's
// legs — so a round trip whose DEPART fare came from the Business search (the fare
// modal re-runs the search per cabin, see cabinSearch) and whose RETURN fare came
// from the Economy one is refused with "Number of PriceId passed in the review
// request doesn't match with the requested trips". Nothing on a fare says which
// search minted it, so every fare is stamped here and the legs of a trip are kept
// on one stamp (FareOptionsModal onPick, FlightResults alignLegsToSearch).
// Keyed on the response OBJECT: React Query and the cabin cache both hand back the
// same object for the same search, and a re-run is a new object. The per-page-load
// prefix stops a stamp that survived a reload (sessionStorage) from matching a
// fresh search once the counter starts over.
const searchTags = new WeakMap();
const SEARCH_TAG_PREFIX = Math.random().toString(36).slice(2, 8); // 6 random chars
let searchTagSeq = 0;

// WHEN a search's keys stop being bookable, keyed by that search's tag. The
// backend stamps `fareValidity` on every /air-search response (booking_backend
// utils/fareWindow.js) from the moment the SUPPLIER answered, so a response
// served from its 45-second cache correctly reports a deadline that is already
// that much nearer — the priceIds in it really are that much older.
//
// Keyed on the TAG rather than carried as one more threaded parameter because
// "which search minted this" is a question every fare and every card already
// answers: a deadline belongs to a SEARCH, and searchTag already names one.
// A plain Map (not a WeakMap) because the key is a string; it holds one number
// per search of a page's life, which is nothing.
const searchDeadlines = new Map();

/**
 * When the fares of the search tagged `tag` should be treated as gone.
 * @param {string} tag  a searchTagOf() value.
 * @returns {number|null} epoch ms, or null when that search carried no stamp
 *          (an older backend) — callers must treat null as "no deadline known"
 *          and NOT as "expired".
 */
export function fareDeadlineFor(tag) {
  return searchDeadlines.get(tag) || null;
}

/**
 * Stable id of one /air-search response — '' when there is none.
 * Also records that search's fare deadline (see searchDeadlines).
 * @param {object} resp  the response object exactly as React Query / cabinSearch hold it.
 * @returns {string}
 */
export function searchTagOf(resp) {
  if (!resp || typeof resp !== 'object') return '';
  if (!searchTags.has(resp)) {
    searchTagSeq += 1;
    searchTags.set(resp, `${SEARCH_TAG_PREFIX}-${searchTagSeq}`);
  }
  const tag = searchTags.get(resp);
  if (!searchDeadlines.has(tag)) {
    const exp = Number(searchRoot(resp)?.fareValidity?.expiresAt) || 0;
    if (exp > 0) searchDeadlines.set(tag, exp);
  }
  return tag;
}

/**
 * Adapt one trip's totalPriceList into the fare-card objects the modal renders.
 * `searchTag` — searchTagOf() of the response the trip came from — rides on every
 * fare, so the legs of a trip can be kept on one search.
 */
export function faresOf(trip, pax, searchTag = '') {
  return (trip?.totalPriceList || [])
    .map((o) => optionTotals(o, pax))
    .filter((o) => o.customer > 0 && o.priceId)
    .sort((a, b) => a.customer - b.customer)
    .map((o, i) => ({
      priceId: o.priceId,
      name: o.name || `Fare ${i + 1}`,
      fareIdentifier: String(o._opt?.fareIdentifier || '').trim(),
      // Special Return pairing — see specialReturnIssue().
      sri: o._opt?.sri ?? null,
      msri: Array.isArray(o._opt?.msri) ? o._opt.msri : [],
      airlineCode: trip?.sI?.[0]?.fD?.aI?.code || '',
      price: o.adultFP || o.customer,
      total: o.customer,
      supplierPrice: o.supplier,
      base: o.base,
      taxes: o.taxes,
      cabin: o.cabinClass,
      refundable: o.refundable,
      seats: o.seats,
      mealIncluded: o.mealIncluded,
      baggage: o.baggage,
      baggageByPax: o.baggageByPax,
      perPax: o.perPax,
      // The search that minted THIS priceId — its own stamp when the page merged
      // several fare-type searches (searchFlightsByFareTypes), else the response
      // this trip was read from.
      searchTag: o._opt?._searchTag || searchTag,
      // When THIS fare's key stops being bookable — the deadline of the search
      // that minted it, not of the page. A fare from the modal's cabin re-search
      // is younger than the list behind it and says so. null = not known.
      expiresAt: fareDeadlineFor(o._opt?._searchTag || searchTag),
      // The fare type the fare was searched under (REGULAR / STUDENT /
      // SENIOR_CITIZEN); "" for a response nobody stamped (the fare modal's
      // cabin re-search), which reads as the search's own type downstream.
      pft: o._opt?._pft || '',
    }));
}

// TripJack returns fares ONLY for the cabin you searched. A flight that also
// sells Business / Premium Economy / First therefore looks Economy-only after an
// Economy search. To show every cabin on the fare-options modal we re-run the
// same search per cabin and pick out the SAME flight by its signature.
//
// Responses are cached for the life of the page, keyed by the exact search +
// cabin, so opening a second flight's modal costs nothing.
const OTHER_CABINS = ['PREMIUM_ECONOMY', 'BUSINESS', 'FIRST'];
const cabinSearchCache = new Map();

function cabinSearch(searchArgs, cabinClass) {
  const key = JSON.stringify({ ...searchArgs, cabinClass });
  if (!cabinSearchCache.has(key)) {
    cabinSearchCache.set(
      key,
      searchFlights(buildAirSearchQuery({ ...searchArgs, cabinClass })).catch(() => null),
    );
  }
  return cabinSearchCache.get(key);
}

/**
 * Fares for `signature` in every cabin OTHER than the one already searched.
 * Returns a flat fare array (possibly empty). Never throws — a cabin whose
 * search fails is simply absent, and the modal shows the cabins it did get.
 *
 * @param {object} args
 * @param {object} args.searchArgs  The same object passed to buildAirSearchQuery
 *                                  for the original search (minus cabinClass).
 * @param {string} args.signature   tripSignature() of the flight the user clicked.
 * @param {object} args.pax         { ADULT, CHILD, INFANT }.
 * @param {string} args.searchedCabin  Cabin already on the card (skipped).
 * @param {string} args.direction   Which leg of the response the flight lives in:
 *                                  'ONWARD' | 'RETURN' for a round trip, or the
 *                                  routeInfo INDEX ('0','1','2'…) for multi-city.
 *                                  A round trip's return leg is NOT under ONWARD,
 *                                  so hardcoding it would silently find nothing.
 */
export async function fetchOtherCabinFares({ searchArgs, signature, pax, searchedCabin = 'ECONOMY', direction = 'ONWARD' }) {
  if (!searchArgs || !signature) return [];
  const cabins = OTHER_CABINS.filter((c) => c !== String(searchedCabin || '').toUpperCase());
  const results = await Promise.all(cabins.map((c) => cabinSearch(searchArgs, c)));

  const out = [];
  for (const resp of results) {
    // Same key resolution adaptSearchResults uses: TripJack keys tripInfos by
    // routeInfo index, but accept the named keys too.
    const tripInfos = searchRoot(resp)?.searchResult?.tripInfos || {};
    const trips = tripInfos[direction] || tripInfos[DIRECTION_INDEX[direction]] || [];
    const trip = trips.find((t) => tripSignature(t) === signature);
    // Stamped with THIS cabin search, not the results page's: its priceIds can
    // only be booked alongside other fares from the same response.
    // cabinSearch runs under the search's PRIMARY fare type only (`fareType`), so
    // name it on the fares — otherwise a picked Business fare would inherit the
    // card's own type (a Student card must not make its Business fare a Student one).
    const pft = normalizeFareTypes(searchArgs.fareType)[0];
    if (trip) out.push(...faresOf(trip, pax, searchTagOf(resp)).map((f) => ({ ...f, pft: f.pft || pft })));
  }
  return out;
}

// `searchTag` — searchTagOf() of the response `trip` came from (see faresOf).
export function toFlightCard(trip, pax, id, cabin = 'ECONOMY', searchTag = '') {
  const segs = trip?.sI || [];
  const s0 = segs[0] || {};
  const sl = segs[segs.length - 1] || s0;
  const aiCode = s0.fD?.aI?.code || '';
  const aiName = s0.fD?.aI?.name || aiCode || 'Airline';
  const brand = brandFor(aiCode);
  const depISO = s0.dt;
  const arrISO = sl.at;
  const mins = tripDurationMinutes(segs);

  const opts = (trip?.totalPriceList || [])
    .map((o) => optionTotals(o, pax))
    .filter((o) => o.customer > 0 && o.priceId)
    .sort((a, b) => a.customer - b.customer);
  const best = opts[0] || {};

  const stops = Math.max(0, segs.length - 1);
  const fromTerm = s0.da?.terminal ? ` T${String(s0.da.terminal).replace(/\D/g, '')}` : '';
  const toTerm = sl.aa?.terminal ? ` T${String(sl.aa.terminal).replace(/\D/g, '')}` : '';

  return {
    id,
    // Cross-cabin identity: lets the fare modal find THIS flight inside a
    // Business / Premium-Economy / First search response.
    sig: tripSignature(trip),
    airline: aiName,
    sub: stops ? `${stops} stop${stops > 1 ? 's' : ''}` : '',
    code: `${aiCode}${s0.fD?.fN || ''}`,
    logo: brand.logo,
    color: brand.color,
    dep: timeOf(depISO),
    arr: timeOf(arrISO),
    from: `${s0.da?.code || ''}${fromTerm}`.trim(),
    to: `${sl.aa?.code || ''}${toTerm}`.trim(),
    fromCode: s0.da?.code || '',
    toCode: sl.aa?.code || '',
    depISO,
    arrISO,
    dur: durLabel(mins),
    durMin: mins,
    stops: stops ? `${stops} stop${stops > 1 ? 's' : ''}` : 'Direct',
    next: dayDiff(depISO, arrISO) > 0,
    // headline price = per-adult marked-up FP of the cheapest fare option
    price: best.adultFP || best.customer || 0,
    // live booking context
    total: best.customer || 0,
    supplierPrice: best.supplier || 0,
    baseFare: best.base || 0,
    taxes: best.taxes || 0,
    priceId: best.priceId || null,
    fareName: best.name || 'Published',
    cabinName: CABIN_LABEL[cabin] || 'Economy',
    seatsLeft: best.seats > 0 ? best.seats : null,
    // Booking class (RBD) of the fare this card is priced on — "EC", "XN", …
    bookingClass: best.bookingClass || null,
    baggage: best.baggage || null, // { checkIn, cabin } of the cheapest fare
    // Per-pax-type rows for the Baggage Information tab, cheapest fare — the
    // fallback for a card with no fare objects (see FlightCard's baggage tab).
    baggageByPax: best.baggageByPax || null,
    included: true,
    deal: '',
    cabinClass: best.cabinClass || '',
    // The fare type of the fare this card is priced on — see faresOf.
    fareType: best._opt?._pft || '',
    // Single source of truth for the fare objects (also used to merge the other
    // cabins' fares into this flight — see fetchOtherCabinFares).
    fares: faresOf(trip, pax, searchTag),
    searchTag: best._opt?._searchTag || searchTag,
    // The card's own deadline, for the same reason its fares carry one.
    expiresAt: fareDeadlineFor(best._opt?._searchTag || searchTag),
    _raw: trip,
  };
}

// The air-search payload is normally the bare TripJack object
// ({ searchResult: { tripInfos } }), but tolerate a { data: … } wrapper too.
function searchRoot(resp) {
  if (resp?.searchResult?.tripInfos) return resp;
  if (resp?.data?.searchResult?.tripInfos) return resp.data;
  return resp || null;
}

// TripJack keys tripInfos by routeInfo index ("0" = first leg, "1" = second),
// not by ONWARD/RETURN. Map the logical direction to the index, but still accept
// the named keys in case a different env returns them.
const DIRECTION_INDEX = { ONWARD: '0', RETURN: '1' };

// ---- ONE CARD PER PHYSICAL FLIGHT --------------------------------------------
//
// TripJack answers a search with one tripInfo per (flight × content source), so
// the SAME flight routinely arrives several times — AI2430 on 15 Sept at 10:00
// once for its PUBLISHED / ECO CLASSIC fares, again for NDC_VALUE, again for SME —
// and each used to become its own card. They are merged here into one card whose
// `fares` holds every fare family on offer for that flight.
//
// "Same flight" = same `sig`. tripSignature() is every segment's airline + flight
// number + departure timestamp (date AND time), joined — for a direct flight that
// is exactly date + flight number + departure time. A connection only merges when
// its WHOLE segment chain matches, so an AI2430 non-stop never absorbs an
// AI2430-then-AI603 connection. The airline code is in it because a flight number
// is only unique within one carrier.
//
// Entries of one flight can still DISAGREE on the rest of the schedule: the
// supplier has returned the same AI2430 10:00 with a 12:15 arrival on one entry
// and 12:20 on another. The headline card is the entry carrying the cheapest
// fare. Every fare that came from a DIFFERENT entry keeps that entry's own trip
// on `src`, because booking persists the segments from the card's `_raw.sI`
// (FlightPayment → the voucher): folding such a fare into the headline card would
// ticket one schedule and print the other. cardForFare() resolves it, and every
// path that turns a picked fare into a booked card goes through it.

// Card fields that describe the FARE, or the card's identity, rather than the
// trip. Everything else toFlightCard() produces is trip data and rides on `src`.
const NON_TRIP_FIELDS = ['id', 'sig', 'fares', 'price', 'total', 'supplierPrice', 'baseFare',
  'taxes', 'priceId', 'fareName', 'seatsLeft', 'bookingClass', 'baggage', 'baggageByPax',
  'cabinClass'];

function tripPartOf(card) {
  const out = { ...card };
  for (const k of NON_TRIP_FIELDS) delete out[k];
  return out;
}

/**
 * Merge cards that are the same physical flight (same `sig`) into one card each.
 * The entry with the cheapest fare supplies the headline (id, price, schedule);
 * `fares` becomes the union of every entry's fares, cheapest first, de-duplicated
 * by priceId. A fare from a non-headline entry carries that entry's trip on `src`.
 * Output order follows each flight's first appearance; a card with no sig is
 * never merged with anything.
 *
 * Across FARE-TYPE searches (searchFlightsByFareTypes) the same fare can come back
 * twice — a Student search may also answer with the flight's ordinary fares. A
 * fare identical to one already kept from a DIFFERENT fare type (same family,
 * cabin and total) is not shown a second time: the first copy stays in `fares`
 * (Regular is searched first, and an identical price is not a concession) and the
 * others move to `shadowFares`. They are still real, bookable priceIds of their
 * own search — alignLegsToSearch reads them to keep a trip on one search. Fares of
 * ONE fare type are never collapsed, so a single-type search is unchanged.
 */
const sameFareKey = (f) => [f.fareIdentifier || f.name || '', f.cabin || '', f.total || 0].join('|');

// A card whose headline fare was one of the collapsed copies is re-pointed at the
// first fare still shown (same total by construction), so clicking the card face
// books the fare the customer can see — never a Student-search copy of a Regular
// fare, which would then ask them for a student ID.
function withShownHeadline(card, shown, shadow) {
  const out = shadow.length ? { ...card, fares: shown, shadowFares: shadow } : { ...card, fares: shown };
  if (!shadow.length || !shown.length || shown.some((f) => f.priceId === card.priceId)) return out;
  const f = shown[0];
  return {
    ...cardForFare(out, f),
    fares: shown,
    shadowFares: shadow,
    priceId: f.priceId,
    price: f.price,
    total: f.total,
    supplierPrice: f.supplierPrice,
    baseFare: f.base,
    taxes: f.taxes,
    fareName: f.name,
    fareType: f.pft || '',
    searchTag: f.searchTag || '',
  };
}

function collapseFareTypeDuplicates(fares) {
  const kept = new Map(); // sameFareKey → pft of the copy shown
  const shown = [];
  const shadow = [];
  for (const f of fares) {
    const key = sameFareKey(f);
    if (kept.has(key) && kept.get(key) !== (f.pft || '')) {
      shadow.push(f);
    } else {
      if (!kept.has(key)) kept.set(key, f.pft || '');
      shown.push(f);
    }
  }
  return { shown, shadow };
}

export function groupFlightCards(cards) {
  const groups = new Map();
  (cards || []).forEach((c, i) => {
    const key = c?.sig || `#nosig:${i}`;
    const g = groups.get(key);
    if (g) g.push(c); else groups.set(key, [c]);
  });
  return [...groups.values()].map((entries) => {
    if (entries.length === 1) {
      const { shown, shadow } = collapseFareTypeDuplicates(entries[0].fares || []);
      return shadow.length ? withShownHeadline(entries[0], shown, shadow) : entries[0];
    }
    // By `total` (the all-pax price fares are ordered on), so the headline entry
    // is the one that owns fares[0] — the fare the card face actually shows.
    const head = entries.reduce((a, b) => ((b.total || 0) < (a.total || 0) ? b : a));
    const seen = new Set();
    const fares = [];
    for (const e of entries) {
      const src = e === head ? null : tripPartOf(e);
      for (const f of e.fares || []) {
        if (!f?.priceId || seen.has(f.priceId)) continue;
        seen.add(f.priceId);
        fares.push(src ? { ...f, src } : f);
      }
    }
    // Collapsed BEFORE the sort, so "first" means the fare-type search order
    // (Regular first), not whichever copy happened to sort ahead.
    const { shown, shadow } = collapseFareTypeDuplicates(fares);
    shown.sort((a, b) => (a.total || 0) - (b.total || 0));
    return withShownHeadline(head, shown, shadow);
  });
}

/**
 * The card a chosen fare should be BOOKED on. A fare merged in from another entry
 * of the same flight (see groupFlightCards) carries that entry's trip on `src`,
 * and books on it; any other fare books on the card as-is. `src` never holds
 * id / sig / fares, so the merged card's identity survives — selection matching
 * (id + priceId) is unaffected.
 */
export function cardForFare(card, fare) {
  return fare?.src ? { ...card, ...fare.src } : card;
}

/**
 * Adapt a full /air-search response into a list of flight cards for one
 * direction (ONWARD by default) — ONE card per physical flight, see
 * groupFlightCards(). Returns [] when nothing came back.
 * A COMBO response has no per-leg keys at all, so it returns [] here by design
 * — those searches go through comboSearchInfo() below instead.
 */
export function adaptSearchResults(resp, pax, direction = 'ONWARD', cabin = 'ECONOMY') {
  const tripInfos = searchRoot(resp)?.searchResult?.tripInfos || {};
  const trips = tripInfos[direction] || tripInfos[DIRECTION_INDEX[direction]] || [];
  // Every leg read from one response shares its stamp, which is what lets the
  // results page tell a leg from this search apart from one picked elsewhere.
  const searchTag = searchTagOf(resp);
  return groupFlightCards(trips
    .filter((t) => !hasRetiredCarrier(t))
    .map((t, i) => toFlightCard(t, pax, `tl_${direction}_${i}`, cabin, searchTag)));
}

// ---- COMBO (international round trip / multi-city) ---------------------------
//
// International searches do NOT come back per leg. The whole search arrives in
// ONE list under `COMBO`, and each entry is a COMPLETE journey covering EVERY
// requested leg — one `sI`, one fare, one priceId. It is sold as a unit: you
// cannot take the outbound of one entry and the return of another.
//
// Treating that list as if it were per-leg boxes is what produced the live
// ₹47,029.28-quoted / ₹17,318.28-charged checkout: two different complete
// journeys were selected as if they were two legs, their two whole-trip fares
// were added together, and the supplier then priced only one of them (the
// second itinerary silently left the cart — CART_CHANGED caught it).
//
// So the response is modelled here as what it is: a set of PAIRS. Each combo
// yields one card per requested leg (all sharing that combo's single priceId
// and fare), plus the leg signatures that identify it. The results page uses
// `combos` to keep the two columns pair-locked — picking an onward narrows the
// return column to the returns actually sold with it — and books the pair as
// ONE supplier trip.

/**
 * Split one combo itinerary into the legs the customer actually searched.
 *
 * Walks the segments and closes a leg once it has arrived at that leg's
 * requested destination AND the next segment departs on/after the next leg's
 * travel date. Airport codes are matched on cityCode too, because the supplier
 * routinely answers a Dubai leg with Fujairah (FJR) or the Dubai bus station
 * (XNB) — the same city, a different code.
 *
 * `isRs` (TripJack's own "this is the return part" flag) is only the FALLBACK:
 * on a multi-city response it is unreliable — 367 of 459 live entries carried
 * no `isRs` at all, which is exactly how a whole round trip ended up rendered
 * as a single 44h "flight".
 *
 * @param {object} trip  one COMBO entry.
 * @param {Array<{to: string, date: string}>} legs  the searched legs, in order.
 * @returns {Array<Array<object>>|null} one segment group per requested leg, or
 *          null when the itinerary cannot be split with confidence (the caller
 *          then drops it rather than showing a guess).
 */
function splitComboLegs(trip, legs) {
  const segs = trip?.sI || [];
  const n = (legs || []).length;
  if (n < 2 || segs.length < n) return null;

  const dayOf = (iso) => String(iso || '').slice(0, 10);
  const codesOf = (place) => [place?.code, place?.cityCode].filter(Boolean).map((c) => String(c).toUpperCase());
  const complete = (groups) => (groups.every((g) => g.length) ? groups : null);

  // 1. By requested destination. Closes a leg once it has arrived where the
  //    customer asked to go and the next segment departs on/after the next
  //    leg's date (so a connection that crosses midnight stays put).
  const byDest = () => {
    const groups = Array.from({ length: n }, () => []);
    let leg = 0;
    segs.forEach((s, i) => {
      groups[leg].push(s);
      if (leg >= n - 1) return;
      const dest = String(legs[leg]?.to || '').toUpperCase();
      const next = segs[i + 1];
      if (!next || !dest || !codesOf(s.aa).includes(dest)) return;
      const nextLegDay = dayOf(legs[leg + 1]?.date);
      if (nextLegDay && dayOf(next.dt) < nextLegDay) return;
      leg += 1;
    });
    return complete(groups);
  };

  // 2. By travel date. The supplier routinely answers a DXB leg with a nearby
  //    airport it considers equivalent — 39 of 427 live journeys flew DEL→AUH
  //    for a DEL→DXB leg — so the destination never matches and rule 1 cannot
  //    close the leg. The dates still separate them: a segment departing on or
  //    after the next leg's travel date starts that leg. Only used when the
  //    legs are on strictly increasing dates, otherwise it cannot tell them
  //    apart (a same-day multi-city hop).
  const byDate = () => {
    for (let i = 1; i < n; i += 1) {
      const prev = dayOf(legs[i - 1]?.date);
      const cur = dayOf(legs[i]?.date);
      if (!prev || !cur || cur <= prev) return null;
    }
    const groups = Array.from({ length: n }, () => []);
    let leg = 0;
    for (const s of segs) {
      while (leg < n - 1 && groups[leg].length && dayOf(s.dt) >= dayOf(legs[leg + 1].date)) leg += 1;
      groups[leg].push(s);
    }
    return complete(groups);
  };

  // 3. The supplier's own return flag. Last, because it is absent on most
  //    multi-city entries — trusting it alone is what rendered a whole round
  //    trip as one 44h "flight".
  const byReturnFlag = () => {
    if (n !== 2 || !segs.some((s) => s.isRs)) return null;
    return complete([segs.filter((s) => !s.isRs), segs.filter((s) => s.isRs)]);
  };

  return byDest() || byDate() || byReturnFlag();
}

/**
 * Model a COMBO response as pairs. Returns `{ isCombo: false }` for the normal
 * per-leg responses, so callers can simply fall through to adaptSearchResults.
 *
 * The searched legs must be passed IN: /air-search answers with `searchResult` +
 * `status` only — it does not echo the query — so the response alone cannot say
 * where one leg ends and the next begins.
 *
 * @param {Array<{to: string, date: string}>} legs  the searched legs, in order.
 * @returns {{
 *   isCombo: boolean,
 *   legCount: number,
 *   combos: Array<{ id: string, sigs: string[], cards: object[], tripCard: object, price: number, total: number }>,
 *   legCards: object[][],   // per leg, one card per DISTINCT itinerary (cheapest combo kept)
 * }}
 */
export function comboSearchInfo(resp, pax, cabin = 'ECONOMY', legs = []) {
  const empty = { isCombo: false, legCount: 0, combos: [], legCards: [] };
  const root = searchRoot(resp);
  const trips = root?.searchResult?.tripInfos?.COMBO;
  // Tolerate the raw TripJack routeInfos shape too, in case a caller has it.
  const wanted = (legs || []).map((l) => ({
    to: l?.to || l?.toCityOrAirport?.code || '',
    date: l?.date || l?.travelDate || '',
  }));
  if (!Array.isArray(trips) || !trips.length || wanted.length < 2) return empty;

  const n = wanted.length;
  const combos = [];
  trips.forEach((t, i) => {
    // A combo is sold as ONE unit, so a retired carrier on any leg kills the
    // whole journey — there is no partial itinerary to fall back to.
    if (hasRetiredCarrier(t)) return;
    const groups = splitComboLegs(t, wanted);
    if (!groups) return;
    // Every leg card is cut from the SAME supplier trip, so they all carry the
    // same fares/priceId and the same whole-trip total by construction.
    const cards = groups.map((sI, L) => toFlightCard({ ...t, sI }, pax, `tl_c${i}_l${L}`, cabin));
    if (!cards[0]?.priceId) return;
    combos.push({
      id: `c${i}`,
      sigs: cards.map((c) => c.sig),
      cards,
      tripCard: toFlightCard(t, pax, `tl_c${i}`, cabin), // whole journey, for the fare modal
      price: cards[0].price,
      total: cards[0].total,
    });
  });
  if (!combos.length) return empty;

  // One card per DISTINCT itinerary on each leg — the same outbound reappears in
  // every combo it is sold with (459 combos are only ~100 distinct legs), so the
  // cheapest combo containing it represents it in the picker.
  const legCards = Array.from({ length: n }, (_, L) => {
    const bySig = new Map();
    for (const c of combos) {
      const prev = bySig.get(c.sigs[L]);
      if (!prev || (c.price || 0) < (prev.price || 0)) bySig.set(c.sigs[L], { ...c.cards[L], comboId: c.id });
    }
    return [...bySig.values()];
  });

  return { isCombo: true, legCount: n, combos, legCards };
}

/**
 * When /air-search returns 200 but the body is a TripJack error / empty payload
 * (no searchResult), pull out a human-readable reason so the UI can explain why
 * instead of a flat "No flights found". Returns null for a valid result.
 */
export function searchResultError(resp) {
  if (!resp || typeof resp !== 'object') return null;
  if (searchRoot(resp)?.searchResult?.tripInfos) return null; // valid result
  const root = resp.data && typeof resp.data === 'object' ? resp.data : resp;
  const e =
    root.errors?.[0]?.message ||
    root.error?.message ||
    (typeof root.error === 'string' ? root.error : null) ||
    root.message ||
    root.status?.message ||
    (root.status && root.status.success === false
      ? 'The flight provider returned an error for this search.'
      : null);
  if (e) return typeof e === 'string' ? e : JSON.stringify(e);
  // Has a body but no recognisable flights and no error markers.
  return null;
}

/**
 * Supplier validation messages, rewritten for a traveller.
 *
 * TripJack reports validation failures as "<field path> : <message>", e.g.
 *   "searchQuery.paxInfo : Infant can't be greater than no of adults."
 *   "searchQuery.routeInfos : Travel date should be in ascending order ."
 * The field path is internal plumbing and the wording is aimed at an integrator,
 * so a customer should never see either. Known cases get plain copy that says
 * what to DO about it; anything unrecognised keeps the supplier's sentence but
 * loses the field prefix, so a new error is still readable rather than raw.
 */
const SUPPLIER_ERROR_COPY = [
  [/infants?\b.*\b(greater|more)\b.*\badults?\b/i,
    'This search has more infants than adults. Every infant under 2 travels on an adult’s lap, so add another adult or remove an infant, then search again.'],
  [/travel date should be in ascending order|next travel date can.?t be less than previous/i,
    'The travel dates are out of order. Each leg has to depart on or after the leg before it — check the dates and search again.'],
  [/date.*(in the past|before today)|past date/i,
    'That departure date has already passed. Pick a date from today onwards and search again.'],
];

export function friendlyFlightError(raw) {
  if (!raw) return null;
  const text = String(raw).trim();
  if (!text) return null;
  for (const [pattern, copy] of SUPPLIER_ERROR_COPY) {
    if (pattern.test(text)) return copy;
  }
  // Strip a leading "searchQuery.paxInfo : " style field path.
  return text.replace(/^[\w.[\]]+\s*:\s*/, '').trim() || text;
}
